import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FiInfo, FiChevronDown, FiChevronUp, FiPlay } from "react-icons/fi";
import type { Step } from "react-joyride";
import { FeatureGuide, useFeatureGuide } from "guides/components";
import { cn } from "utils/helpers";

type BaseProps = {
	"data-testid"?: string;
	/** Joyride steps — when provided, renders a "Show Me" button and manages the tour internally. */
	guideSteps?: Step[];
	/** Label for the guide button (default "Show Me"). */
	showGuideLabel?: string;
	/** Fired after "Show Me" is clicked but before the tour starts. Return false to cancel. */
	onBeforeShowMe?: () => boolean | void | Promise<boolean | void>;
	/** Fired once the tour finishes or is skipped. */
	onAfterShowMe?: () => void;
	/** When true, the body is hidden behind a toggle header. */
	collapsible?: boolean;
	/** Only used when `collapsible` is true — start expanded? (default false) */
	defaultExpanded?: boolean;
	footer?: React.ReactNode;
	title?: string;
};

type WithListProps = BaseProps & {
	children?: never;
	inline?: false;
	instructions: React.ReactNode[];
	variant?: "bulleted" | "numbered";
};

type InlineProps = BaseProps & {
	children: React.ReactNode;
	inline: true;
	instructions?: never;
	variant?: never;
};

type WithChildrenProps = BaseProps & {
	children: React.ReactNode;
	inline?: false;
	instructions?: never;
	variant?: never;
};

type InstructionsInfoBoxProps = WithListProps | InlineProps | WithChildrenProps;

export const InstructionsInfoBox: React.FC<InstructionsInfoBoxProps> = ({
	"data-testid": dataTestId,
	title = "What you need to do",
	collapsible = true,
	defaultExpanded = false,
	inline = false,
	instructions,
	variant = "bulleted",
	footer,
	guideSteps,
	showGuideLabel = "Show Me",
	onBeforeShowMe,
	onAfterShowMe,
	children,
}) => {
	const internalGuide = useFeatureGuide();
	const hasGuide = !!(guideSteps?.length);

	// Two-phase mount: Joyride must see run transition from false → true.
	// Phase 1 (mounted=true, run=false): mount Joyride into the DOM.
	// Phase 2 (next frame, run=true): Joyride sees the transition and starts.
	const [guideMounted, setGuideMounted] = useState(false);
	const [guideRun, setGuideRun] = useState(false);

	const handleShowGuide = useCallback(async () => {
		if (!guideSteps?.length) return;
		if (onBeforeShowMe) {
			const result = await onBeforeShowMe();
			if (result === false) return;
		}
		// Phase 1: mount Joyride with run=false
		setGuideMounted(true);
		setGuideRun(false);
	}, [guideSteps, onBeforeShowMe]);

	// Phase 2: once mounted, start on next frame
	useEffect(() => {
		if (guideMounted && !guideRun) {
			const frame = requestAnimationFrame(() => {
				setGuideRun(true);
				internalGuide.start();
			});
			return () => cancelAnimationFrame(frame);
		}
	}, [guideMounted, guideRun, internalGuide]);

	// Unmount Joyride when the tour finishes
	useEffect(() => {
		if (guideMounted && !internalGuide.isRunning && guideRun) {
			setGuideMounted(false);
			setGuideRun(false);
			onAfterShowMe?.();
		}
	}, [guideMounted, guideRun, internalGuide.isRunning, onAfterShowMe]);

	// default expanded if inline or if not collapsible or the defaultExpanded is set, otherwise default to collapsed
	const [expanded, setExpanded] = useState(inline || (!collapsible || defaultExpanded));
	const isInline = inline === true;
	const hasInstructions = instructions && instructions.length > 0;

	const ListTag =
		hasInstructions && variant === "numbered" ? "ol" : "ul";

	const headerContent = (
		<div className="flex items-center gap-2 text-sm font-medium text-gray-900">
			<FiInfo size={16} className="shrink-0 text-[#0031DD]" aria-hidden="true" />
			<span className="flex-1">{title}</span>
			{collapsible && (
				expanded
					? <FiChevronUp size={16} className="shrink-0 text-gray-400" />
					: <FiChevronDown size={16} className="shrink-0 text-gray-400" />
			)}
		</div>
	);

	const bodyContent = (
		<>
			{/* LIST MODE */}
			{hasInstructions && (
				<ListTag
					className={`space-y-2 pl-6 text-sm text-gray-600 ${variant === "numbered"
						? "list-decimal"
						: "list-none"
						}`}
				>
					{instructions.map((item, index) => (
						<li key={index} className="flex items-start gap-2">
							{variant !== "numbered" && (
								<span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0031DD]" />
							)}
							<span>{item}</span>
						</li>
					))}
				</ListTag>
			)}

			{/* FALLBACK CONTENT */}
			{children && (
				<div className="text-sm text-gray-600">{children}</div>
			)}

			{/* SHOW ME BUTTON */}
			{hasGuide && (
				<div className="mt-3">
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							handleShowGuide();
						}}
						className="inline-flex items-center gap-1.5 rounded-md border border-[#0031DD]/30 bg-white px-3 py-1.5 text-xs font-medium text-[#0031DD] shadow-sm transition-colors hover:bg-[#0031DD]/5 hover:border-[#0031DD]/50"
					>
						<FiPlay size={12} />
						{showGuideLabel}
					</button>
				</div>
			)}

			{/* FOOTER */}
			{footer && (
				<div className="mt-3 border-t border-[#0031DD]/10 pt-3 text-xs text-gray-600">
					{footer}
				</div>
			)}
		</>
	);

	return (
		<>
			<div className={cn("rounded-lg border border-[#0031DD]/20 bg-[#f5f7ff] px-4 py-3", !expanded && !isInline && "hover:bg-blue-200 cursor-pointer")} data-testid={dataTestId} onClick={() => setExpanded((prev) => !prev)}>
				{/* INLINE MODE */}
				{isInline ? (
					<div className="flex items-center gap-2 text-sm text-gray-700">
						<FiInfo size={16} className="shrink-0 text-[#0031DD]" />
						<span>{children}</span>
					</div>
				) : collapsible ? (
					<>
						<span
							className="w-full text-left cursor-pointer"
							aria-expanded={expanded}
						>
							{headerContent}
						</span>
						{expanded && <div className="mt-3">{bodyContent}</div>}
					</>
				) : (
					<>
						<div className="mb-3">{headerContent}</div>
						{bodyContent}
					</>
				)}
			</div>

			{/* Only portal Joyride into the DOM while the tour is active — prevents body height pollution */}
			{guideMounted && guideSteps && guideSteps.length > 0 &&
				createPortal(
					<FeatureGuide guide={internalGuide} steps={guideSteps} />,
					document.body,
				)
			}
		</>
	);
};
